import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, Table, Badge, Dropdown, Form, Button, Modal, InputGroup } from "react-bootstrap";
import { getUsers, blockUser, deleteUser, updateUserWalletBalance, changeUserPassword } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const Users = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10000,
        total: 0
    });
    const [showWalletModal, setShowWalletModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [walletAmount, setWalletAmount] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [processing, setProcessing] = useState(false);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await getUsers({ page: pagination.page, limit: pagination.limit });
            const usersData = response.data?.users || response.data || [];
            const processedUsers = Array.isArray(usersData) ? usersData : [];
            setUsers(processedUsers);
            setFilteredUsers(processedUsers);
            setSummary(response.data?.summary || {});
            setPagination(prev => ({
                ...prev,
                total: response.data?.pagination?.total || 0
            }));
            setError(null);
        } catch (error) {
            setError(error.message || "Failed to fetch users");
            setUsers([]);
            setFilteredUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [pagination.page, pagination.limit]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
            return;
        }
        const searchValue = searchTerm.toLowerCase().trim();
        const filtered = users.filter(user => 
            user.name?.toLowerCase().includes(searchValue) ||
            user.email?.toLowerCase().includes(searchValue) ||
            user.mobileNumber?.toLowerCase().includes(searchValue)
        );
        setFilteredUsers(filtered);
    }, [searchTerm, users]);

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            inactive: 'warning',
            blocked: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    const handleToggleStatus = async (user) => {
        const action = user.status !== "active" ? "unblock" : "block";
        const confirmMsg = action === "unblock" 
            ? "Do you want to unblock this user?"
            : "Do you want to block this user?";
        
        if (!window.confirm(confirmMsg)) return;

        try {
            await blockUser(user._id, action, action === "block" ? "Blocked by admin" : "Unblocked by admin");
            alert("User status updated successfully");
            fetchUsers();
        } catch (error) {
            alert(error.message || "Failed to update user status");
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm("Are you sure? Once deleted, this user account cannot be recovered!")) return;

        try {
            await deleteUser(userId);
            alert("User has been deleted successfully");
            fetchUsers();
        } catch (error) {
            alert(error.message || "Failed to delete user");
        }
    };

    const handleWalletUpdate = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);
            const amount = parseFloat(walletAmount);
            
            if (isNaN(amount)) {
                throw new Error('Please enter a valid amount');
            }

            await updateUserWalletBalance(selectedUser._id, amount);
            alert("Wallet balance updated successfully");
            setShowWalletModal(false);
            setWalletAmount('');
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            alert(error.message || "Failed to update wallet balance");
        } finally {
            setProcessing(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        try {
            setProcessing(true);
            
            if (!newPassword || newPassword.length < 6) {
                throw new Error('Password must be at least 6 characters long');
            }

            await changeUserPassword(selectedUser._id, newPassword);
            alert("User password has been updated successfully");
            setShowPasswordModal(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (error) {
            alert(error.message || "Failed to change password");
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <div className="container-xxl">
                <div className="d-flex justify-content-center align-items-center min-vh-50">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Users" sidebutton={true} />
            <div className="row">
                <div className="col-lg-12">
                    <div className="row mb-4">
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h3 className="mb-1 fw-bold text-primary">{summary.total || 0}</h3>
                                            <p className="text-muted mb-0 small">Total Users</p>
                                        </div>
                                        <div className="icon-box bg-primary-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa fa-users text-primary" style={{ fontSize: '28px' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h3 className="mb-1 fw-bold text-success">{summary.active || 0}</h3>
                                            <p className="text-muted mb-0 small">Active Users</p>
                                        </div>
                                        <div className="icon-box bg-success-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa fa-user-check text-success" style={{ fontSize: '28px' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h3 className="mb-1 fw-bold text-warning">{summary.inactive || 0}</h3>
                                            <p className="text-muted mb-0 small">Inactive Users</p>
                                        </div>
                                        <div className="icon-box bg-warning-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa fa-user-clock text-warning" style={{ fontSize: '28px' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-3 col-sm-6 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <h3 className="mb-1 fw-bold text-danger">{summary.blocked || 0}</h3>
                                            <p className="text-muted mb-0 small">Blocked Users</p>
                                        </div>
                                        <div className="icon-box bg-danger-light rounded-circle" style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <i className="fa fa-user-times text-danger" style={{ fontSize: '28px' }}></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center py-3">
                            <Card.Title className="mb-0 fw-bold">Users List</Card.Title>
                            <div className="d-flex align-items-center gap-2">
                                <div className="search-box" style={{ width: '300px' }}>
                                    <InputGroup className="border rounded">
                                        <InputGroup.Text className="bg-white border-0">
                                            <i className="fa fa-search text-muted"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            className="border-0"
                                            placeholder="Search by name, email or mobile..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        {searchTerm && (
                                            <InputGroup.Text 
                                                className="bg-white border-0"
                                                onClick={() => setSearchTerm('')}
                                                style={{ cursor: 'pointer' }}
                                                title="Clear search"
                                            >
                                                <i className="fa fa-times text-muted"></i>
                                            </InputGroup.Text>
                                        )}
                                    </InputGroup>
                                </div>
                                <Link to="/admin/add-user" className="btn btn-primary d-flex align-items-center gap-2">
                                    <i className="fa fa-plus"></i> Add User
                                </Link>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {error && <div className="alert alert-danger m-3">{error}</div>}
                            <div className="table-responsive">
                                <Table responsive className="table-hover mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="fw-semibold">Name</th>
                                            <th className="fw-semibold">Email</th>
                                            <th className="fw-semibold">Mobile</th>
                                            <th className="fw-semibold">Agent ID</th>
                                            <th className="fw-semibold">Wallet Balance</th>
                                            <th className="fw-semibold">Status</th>
                                            <th className="fw-semibold">Password</th>
                                            <th className="fw-semibold">Joined Date</th>
                                            <th className="fw-semibold text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" className="text-center py-5">
                                                    <div className="text-muted">
                                                        <i className="fa fa-users fa-3x mb-3 d-block"></i>
                                                        <p className="mb-0">{searchTerm ? "No users found matching your search." : "No users available."}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredUsers.map((user) => (
                                                <tr key={user._id} className="align-middle">
                                                    <td className="fw-medium">{user.name || '-'}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span>{user.email || '-'}</span>
                                                            {user.isEmailVerified && (
                                                                <i className="fa fa-check-circle text-success ms-2" title="Email Verified"></i>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span>{user.mobileNumber || '-'}</span>
                                                            {user.isMobileVerified && (
                                                                <i className="fa fa-check-circle text-success ms-2" title="Mobile Verified"></i>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {user.agent ? (
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-medium">{user.agent.agentId || user.agent._id || '-'}</span>
                                                                {user.agent.status && (
                                                                    <Badge bg={getStatusBadge(user.agent.status)} className="px-2 py-1">
                                                                        {user.agent.status}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="fw-semibold">{formatAmount(user.wallet?.amount || user.wallet?.balance || 0)}</td>
                                                    <td>
                                                        <Badge bg={getStatusBadge(user.status)} className="px-3 py-2">
                                                            {user.status || 'N/A'}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <span className="font-monospace">{user.passwordPlain || '-'}</span>
                                                    </td>
                                                    <td className="text-muted">{formatDate(user.createdAt)}</td>
                                                    <td className="text-center">
                                                        <Dropdown>
                                                            <Dropdown.Toggle variant="primary" size="sm" className="light sharp">
                                                                <i className="fa fa-ellipsis-v"></i>
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu className="shadow-sm border" align="end">
                                                                <Dropdown.Item 
                                                                    onClick={() => navigate(`/admin/user/${user._id}`)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <i className="fa fa-eye me-2"></i> View Details
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={() => navigate(`/admin/edit-user/${user._id}`)}
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    <i className="fa fa-edit me-2"></i> Edit User
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item 
                                                                    onClick={() => handleToggleStatus(user)}
                                                                    className={user.status !== "active" ? "text-success" : "text-danger"}
                                                                >
                                                                    <i className={`fa ${user.status !== "active" ? "fa-unlock" : "fa-lock"} me-2`}></i>
                                                                    {user.status !== "active" ? "Unblock User" : "Block User"}
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={() => {
                                                                        setSelectedUser(user);
                                                                        setWalletAmount('');
                                                                        setShowWalletModal(true);
                                                                    }}
                                                                    className="text-primary"
                                                                >
                                                                    <i className="fa fa-money me-2"></i> Update Wallet
                                                                </Dropdown.Item>
                                                                <Dropdown.Item 
                                                                    onClick={() => {
                                                                        setSelectedUser(user);
                                                                        setNewPassword('');
                                                                        setShowPasswordModal(true);
                                                                    }}
                                                                    className="text-warning"
                                                                >
                                                                    <i className="fa fa-key me-2"></i> Change Password
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                                <Dropdown.Item 
                                                                    onClick={() => handleDelete(user._id)}
                                                                    className="text-danger"
                                                                >
                                                                    <i className="fa fa-trash me-2"></i> Delete User
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Wallet Update Modal */}
            <Modal show={showWalletModal} onHide={() => setShowWalletModal(false)} centered>
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="fw-bold">
                        <i className="fa fa-money me-2 text-primary"></i> Update Wallet Balance
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleWalletUpdate}>
                    <Modal.Body className="py-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-2">User</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedUser?.name || selectedUser?.email || ''}
                                disabled
                                className="bg-light"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-2">Amount</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                placeholder="Enter amount (positive to add, negative to subtract)"
                                value={walletAmount}
                                onChange={(e) => setWalletAmount(e.target.value)}
                                required
                                className="form-control-lg"
                            />
                            <Form.Text className="text-muted">
                                Enter a positive value to add funds or negative value to subtract funds
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-top">
                        <Button variant="secondary" onClick={() => setShowWalletModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={processing}>
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2"></i> Update Balance
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* Password Change Modal */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="fw-bold">
                        <i className="fa fa-key me-2 text-warning"></i> Change Password
                    </Modal.Title>
                </Modal.Header>
                <form onSubmit={handlePasswordChange}>
                    <Modal.Body className="py-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-2">User</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedUser?.name || selectedUser?.email || ''}
                                disabled
                                className="bg-light"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-semibold mb-2">New Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Enter new password (min 6 characters)"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={6}
                                className="form-control-lg"
                            />
                            <Form.Text className="text-muted">
                                Password must be at least 6 characters long
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-top">
                        <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={processing}>
                            {processing ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Changing...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-check me-2"></i> Change Password
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </form>
            </Modal>
            
            <style>{`
                .card {
                    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
                }
                .card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
                }
                .table-hover tbody tr:hover {
                    background-color: #f8f9fa;
                }
                .table th {
                    border-top: none;
                    font-weight: 600;
                    color: #495057;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    letter-spacing: 0.5px;
                }
                .table td {
                    vertical-align: middle;
                    padding: 1rem 0.75rem;
                }
                .search-box .form-control:focus {
                    border-color: #0d6efd;
                    box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
                }
                .dropdown-menu {
                    min-width: 200px;
                }
                .dropdown-item {
                    padding: 0.5rem 1rem;
                    transition: background-color 0.2s;
                }
                .dropdown-item:hover {
                    background-color: #f8f9fa;
                }
                .badge {
                    font-weight: 500;
                    font-size: 0.75rem;
                }
            `}</style>
        </div>
    );
};

export default Users;

